/**
 * Pinterest Advertising Integration Module (Stories 19.4 + 19.5)
 *
 * Barrel export for OAuth, REST helpers, errors, the Pinterest Ads adapter,
 * and the Story-19.5 sync analytics + async report templates pipelines.
 *
 * Pinterest Ads characteristics worth knowing about before reading this code:
 *  - OAuth 2.0 + PKCE against Pinterest API v5
 *    (`https://www.pinterest.com/oauth/` for authorize,
 *     `https://api.pinterest.com/v5/oauth/token` for token)
 *  - HTTP Basic Auth on the token endpoint (NOT form-body credentials —
 *    diverges from MS Ads / Klaviyo, matches Pinterest's documented contract)
 *  - Continuous refresh tokens: 60-day lifetime, refreshable indefinitely,
 *    rotated on every successful refresh (Pinterest June 2025 newsletter)
 *  - Single auth credential per data API call (Bearer access token) — no
 *    Developer Token like Microsoft Ads
 *  - Provider account ID is the lowest-`id` ad account (BigInt-aware sort)
 *    so re-auths produce stable composites without an extra DB lookup
 *
 * The folder is named `pinterest/` (not `pinterest-ads/`) to leave room for
 * future Pinterest properties (Pinterest Trends, organic-only adapters, etc.)
 * to live alongside the Ads adapter, mirroring how `microsoft/` houses MS Ads.
 */

import { registerAdapter } from "../registry";
import { pinterestAdsAdapter } from "./pinterest-ads";

// Register the adapter so getAdapter("pinterest-ads") returns it.
registerAdapter("pinterest-ads", pinterestAdsAdapter);

// Platform values (shared constant for frontend/backend alignment, mirrors MS Ads)
export const PINTEREST_ADS_PLATFORM_VALUES = ["pinterest-ads"] as const;
export type PinterestAdsPlatform =
  (typeof PINTEREST_ADS_PLATFORM_VALUES)[number];

// OAuth utilities
export {
  buildPinterestAdsAuthUrl,
  exchangePinterestAdsCode,
  refreshPinterestAdsToken,
  parsePinterestAdsCallbackError,
  PINTEREST_ADS_SCOPES,
  PINTEREST_ADS_REFRESH_TOKEN_LIFETIME_MS,
  PinterestAdsTokenError,
  type PinterestAdsTokenErrorOptions,
  type PinterestAdsAuthUrlOptions,
  type PinterestAdsTokenResponse,
  type PinterestAdsCallbackError,
} from "./oauth";

// REST helpers
export {
  buildPinterestHeaders,
  fetchPinterestAdsUserAccount,
  fetchPinterestAdsAdAccounts,
  PINTEREST_API_BASE,
  PINTEREST_AD_ACCOUNTS_MAX_PAGES,
  type PinterestUserAccountType,
  type PinterestAdsUserAccount,
  type PinterestAdAccount,
  type PinterestAdAccountsResult,
} from "./api";

// Error parsing
export {
  parsePinterestAdsApiError,
  pinterestAdsErrorTypeToAdapterCode,
  PinterestAdsApiCallError,
  type PinterestAdsApiError,
  type PinterestAdsErrorType,
} from "./errors";

// Adapter
export {
  pinterestAdsAdapter,
  PINTEREST_ADS_METRICS,
  PINTEREST_ADS_DIMENSIONS,
  PINTEREST_ADS_API_VERSION,
  PINTEREST_ADS_REPORT_TYPES,
  PINTEREST_ADS_SYNC_NON_QUERYABLE_METRICS,
  MAX_FETCHMETRICS_DAYS,
  buildPinterestAdsCacheKey,
} from "./pinterest-ads";

// Sync analytics module (Story 19.5)
export {
  PINTEREST_ADS_API_BASE,
  PINTEREST_SYNC_TIMEOUT_MS,
  PINTEREST_ADS_MAX_COLUMNS_PER_REQUEST,
  PINTEREST_ADS_MAX_IDS_PER_REQUEST,
  PINTEREST_ADS_NOT_QUERYABLE_SYNC,
  MAX_HOURLY_GRANULARITY_DAYS,
  MAX_SYNC_ANALYTICS_DAYS,
  MAX_ASYNC_REPORT_DAYS,
  fetchPinterestAdsAccountAnalytics,
  fetchPinterestAdsCampaignsAnalytics,
  fetchPinterestAdsAdGroupsAnalytics,
  fetchPinterestAdsAdsAnalytics,
  fetchPinterestAdsTargetingAnalytics,
  parsePinterestAdsSyncResponse,
  type PinterestAdsReportType,
  type PinterestAdsGranularity,
  type PinterestAttributionWindowDays,
  type PinterestAttributionWindows,
  type PinterestAdsAnalyticsOptions,
  type PinterestAdsAnalyticsResponse,
} from "./analytics";

// Async report templates module (Story 19.5)
export {
  PINTEREST_REPORTS_API_BASE,
  POLL_INTERVAL_MS_INITIAL,
  POLL_INTERVAL_MS_MAX,
  POLL_INTERVAL_MULTIPLIER,
  MAX_POLL_ATTEMPTS_SMALL,
  MAX_POLL_ATTEMPTS_LARGE,
  CREATE_TIMEOUT_MS,
  POLL_TIMEOUT_MS,
  DOWNLOAD_TIMEOUT_MS,
  createPinterestAdsAsyncReport,
  pollPinterestAdsAsyncReport,
  pollPinterestAdsAsyncReportUntilDone,
  downloadPinterestAdsAsyncReport,
  type PinterestAdsAsyncReportLevel,
  type PinterestAdsAsyncReportStatus,
  type PinterestAdsAsyncReportCreateBody,
  type PinterestAdsAsyncReportCreateResponse,
  type PinterestAdsAsyncReportPollResponse,
} from "./reports";

// Report builders (Story 19.5)
export {
  COLUMNS_BY_REPORT_TYPE,
  TARGETING_TYPES_BY_REPORT_TYPE,
  GRANULARITY_BY_REPORT_TYPE,
  OVERVIEW_COLUMNS,
  CAMPAIGNS_COLUMNS,
  AD_GROUPS_COLUMNS,
  ADS_COLUMNS,
  KEYWORDS_COLUMNS,
  AGE_GENDER_COLUMNS,
  GEO_COLUMNS,
  INTERESTS_COLUMNS,
  buildSyncAnalyticsRequest,
  buildAsyncReportBody,
  dateToYearMonthDay,
} from "./report-builders";

// Transformers (Story 19.5)
export {
  parseMetric,
  parseMicroDollar,
  parsePercent,
  parseDuration,
  TRANSFORMERS,
  transformOverviewRows,
  transformCampaignsRows,
  transformAdGroupsRows,
  transformAdsRows,
  transformKeywordsRows,
  transformAgeGenderRows,
  transformGeoRows,
  transformInterestsRows,
} from "./transformers";
